import { Box, Flex } from '@chakra-ui/react';
import React, { useState } from 'react';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { getFromUrl } from '../../api/Api';
import useRangeChange from '../../hooks/useRangeChange';
import { Indexable } from '../../interface';
import { colors } from '../../theme/colors';
import {
  buildAnalyticsQuery,
  isAnalyticsQueryReady
} from '../../utils/dateRange';
import Card from '../Dashboard/Card';
import CardHeader from '../Dashboard/CardHeader';

const GENDER_COLORS: Record<string, string> = {
  FEMALE: colors.accent,
  MALE: '#F2AC57',
  OTHER: colors.dark_green
};

const capitalize = (s: string) =>
  s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  name
}: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 1.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return percent > 0 ? (
    <text
      x={x}
      y={y}
      fill={colors.gray}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline='central'
      fontSize={12}
      fontWeight={500}
    >
      {`${capitalize(name)} ${(percent * 100).toFixed(0)}%`}
    </text>
  ) : null;
};

const GenderRatioChart = () => {
  const [data, setData] = useState<any[]>([]);
  const {
    range,
    startDate,
    endDate,
    handleChange,
    handleStartDateChange,
    handleEndDateChange
  } = useRangeChange();
  const createChartData = (object: any) => {
    const graphData: any[] = [];
    for (const key in object) {
      if (key === 'null') continue;
      if (object[key] === 0) continue;
      const item = {
        name: key,
        value: (object as Indexable)[key]
      };
      graphData.push(item);
    }
    setData(graphData);
  };
  React.useEffect(() => {
    const getGenderRatio = async () => {
      if (!isAnalyticsQueryReady(range, startDate, endDate)) {
        setData([]);
        return;
      }

      try {
        const query = buildAnalyticsQuery(range, startDate, endDate);
        const { result } = await getFromUrl(`users/gender/ratio?${query}`);
        createChartData(result);
      } catch (e: any) {
        return;
      }
    };
    getGenderRatio().catch((err) => console.error(err));
  }, [range, startDate, endDate]);

  return (
    <Card h='auto' py='24px'>
      <Box width='100%'>
        <CardHeader
          title='Gender Ratio'
          handleChange={handleChange}
          value={range}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
        />
        {data.length ? (
          <ResponsiveContainer width='100%' height={300}>
            <PieChart>
              <Pie
                data={data}
                dataKey='value'
                nameKey='name'
                cx='50%'
                cy='50%'
                innerRadius={60}
                outerRadius={100}
                label={renderCustomLabel}
                labelLine={false}
                isAnimationActive={true}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={GENDER_COLORS[entry.name] || colors.dark_green}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: any, name: string) => [
                  value,
                  capitalize(name)
                ]}
                contentStyle={{
                  backgroundColor: colors.white,
                  borderRadius: 8,
                  fontSize: '13px',
                  border: `1px solid ${colors.shade}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              />
              <Legend
                verticalAlign='bottom'
                height={36}
                formatter={(value: string) => (
                  <span style={{ color: colors.gray, fontSize: 12 }}>
                    {capitalize(value)}
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <Flex
            justifyContent='center'
            marginX='auto'
            marginY='40px'
            width='100%'
            color={colors.gray}
          >
            {range === 'CUSTOM' && (!startDate || !endDate)
              ? 'Select start and end dates'
              : 'No data available'}
          </Flex>
        )}
      </Box>
    </Card>
  );
};

export default GenderRatioChart;
